import React, { ChangeEvent, FC, useCallback, useEffect } from 'react';
import Button from '@mui/material/Button';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { Link, useHistory, useParams } from 'react-router-dom';
import { combineComponents } from '../../contexts/combineComponents';
import { ProductToolsFetcher } from '../../fetchers/ProductTools';
import { ProductTools } from './ProductTools';
import { ProductPackages } from './ProductPackages';
import { ProductPackagesFetcher } from '../../fetchers/ProductPackagesFetcher';
import { ProductsFetcher } from '../../fetchers/ProductsFetcher';
import { ProductsList } from './ProductsList';
import { useTranslation } from 'react-i18next';
import styled from '@mui/styles/styled';
import { useQueryUrl } from '../../hooks/useQueryUrl';
import { AuthenticatedRoutes } from '../../routers/AuthenticatedRoutes';
import { apiProduct } from '../../resources/api/apiProduct';
import { apiProductPackage } from '../../resources/api/apiProductPackage';
import { apiProductTool } from '../../resources/api/apiProductTool';
import { useResourceChecker } from '../../resources/resource';
import { PackagesType } from '../../models/PackagesType';
import { ProductsTab } from '../../models/ProductsTab';

interface IUrlParams {
  tab?: ProductsTab;
}

const Providers = combineComponents(ProductToolsFetcher.WMF, ProductPackagesFetcher.WMF, ProductsFetcher.WMF);

const TabsContainer = styled('div')({
  display: 'flex',
});
const StyledTabs = styled(Tabs)({
  flexGrow: 1,
});
const ButtonContainer = styled('div')({
  flex: 1,
  textAlign: 'right',
});

const { useGetProducts, useCreateProduct } = apiProduct;
const { useGetPackages, useCreatePackage } = apiProductPackage;
const { useGetProductTools, useAddTool } = apiProductTool;

export const AdminProducts: FC = () => {
  const { tab } = useParams<IUrlParams>();
  const history = useHistory();
  const { t } = useTranslation();

  const resourceChecker = useResourceChecker();
  const { resource: resourceProducts } = useGetProducts();
  const { resource: resourcePackages } = useGetPackages();
  const { resource: resourceTools } = useGetProductTools();

  const { resource: resourceCreateProducts } = useCreateProduct();
  const { resource: resourceCreatePackages } = useCreatePackage();
  const { resource: resourceCreateTools } = useAddTool();

  const isProductsAllowed = resourceChecker(resourceProducts);
  const isPackagesAllowed = resourceChecker(resourcePackages);
  const isToolsAllowed = resourceChecker(resourceTools);

  const isCreateAllowed =
    (tab === ProductsTab.Tools && resourceChecker(resourceCreateTools)) ||
    (tab === ProductsTab.Packages && resourceChecker(resourceCreatePackages)) ||
    (tab === ProductsTab.Addons && resourceChecker(resourceCreatePackages)) ||
    (tab === ProductsTab.Products && resourceChecker(resourceCreateProducts));

  const onChange = useCallback((e: ChangeEvent<unknown>, tab: ProductsTab) => {
    history.push(AuthenticatedRoutes.AdminProductsTab(tab));
  }, []);

  useEffect(() => {
    if (typeof tab === 'undefined' || !Object.values(ProductsTab).includes(tab)) {
      history.replace(AuthenticatedRoutes.AdminProductsTab(ProductsTab.Products));
    }
  }, [tab]);

  const newItemUrl = useQueryUrl('dialog', 'new');

  return (
    <div>
      <TabsContainer>
        <StyledTabs
          value={tab ?? false}
          indicatorColor="primary"
          textColor="primary"
          onChange={onChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {isProductsAllowed ? <Tab label={t('adminProducts.products.tabTitle')} value={ProductsTab.Products} /> : null}
          {isPackagesAllowed ? <Tab label={t('adminProducts.packages.tabTitle')} value={ProductsTab.Packages} /> : null}
          {isPackagesAllowed ? <Tab label={t('adminProducts.addons.tabTitle')} value={ProductsTab.Addons} /> : null}
          {isToolsAllowed ? <Tab label={t('adminProducts.tools.tabTitle')} value={ProductsTab.Tools} /> : null}
        </StyledTabs>
        <ButtonContainer>
          {isCreateAllowed ? (
            <Button variant="contained" color="primary" component={Link} to={newItemUrl}>
              {tab === ProductsTab.Tools ? t('adminProducts.tools.addButton') : null}
              {tab === ProductsTab.Packages ? t('adminProducts.packages.addButton') : null}
              {tab === ProductsTab.Addons ? t('adminProducts.addons.addButton') : null}
              {tab === ProductsTab.Products ? t('adminProducts.products.addButton') : null}
            </Button>
          ) : null}
        </ButtonContainer>
      </TabsContainer>

      <Providers>
        {tab === ProductsTab.Tools ? <ProductTools /> : null}
        {tab === ProductsTab.Packages ? <ProductPackages packagesType={PackagesType.Packages} /> : null}
        {tab === ProductsTab.Addons ? <ProductPackages packagesType={PackagesType.Addons} /> : null}
        {tab === ProductsTab.Products ? <ProductsList /> : null}
      </Providers>
    </div>
  );
};
